import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { Box, BoxProps, Paper } from '@mui/material'

import { useUserSession, useApiAccess } from 'features/session/context'

import { DashBoardItem } from './components/item'

import {
  DashBoardConditions,
  DashBoardCondition,
  DashBoardConfigure,
  DashBoardItemData
} from './types'

import { LoadFromLocal, LoadFromServer, LoadFromServerByProfileId } from './profile'

import ReactGridLayout from 'react-grid-layout'
import 'react-resizable/css/styles.css'
import 'styles/react-grid-layout.scss'

import { DashBoardValue } from './hooks'
import { SearchDetailResult } from 'interface/search'
import { TargetListResult } from 'interface/search/targetList'

export * from './components/graph'
export type {
  DashBoardConfigure
};

let refConditions: DashBoardConditions | undefined = undefined

type DashBoardProps<T> = {
  dashBoard: DashBoardValue<T>
  search?: SearchDetailResult
  targetList?: TargetListResult
  dataList: T[] | null
  onSelected: (id: string) => void
  defaultProfileId?: number
} & BoxProps

export const DashBoard = <T,>({
  dashBoard,
  search,
  targetList,
  dataList,
  onSelected,
  defaultProfileId,
  ...others
}: DashBoardProps<T>) => {
  const {
    profileKey,
    conditions, setConditions,
    dashBoardItems, onChangeDashBoardItems,
    onLoadProfile, onLoadCookieData
  } = dashBoard

  const { userId } = useUserSession()
  const apiAccess = useApiAccess()

  const [initialized, setInitialized] = useState<boolean>(false)

  const dashBoardRef = useRef<HTMLDivElement | null>(null)
  const [classNames, setClassNames] = useState('layout')
  const [layoutWidth, setLayoutWidth] = useState(0)
  const [itemHeightUnit, setItemHeightUnit] = useState(80)
  const [resizingItemId, setResizingItemId] = useState<string | null>(null)
  const [maximizeItemId, setMaximizeItemId] = useState<number | null>(null)
  
  useEffect(
    () => {
      if (initialized) return

      setInitialized(true)

      LoadFromServerByProfileId(
        apiAccess, profileKey, defaultProfileId,
        (profile) => {
          if (profile?.profileData) {
            onLoadProfile(profile)
          } else {
            LoadFromLocal(userId, profileKey,
              (data) => {
                if (data) {
                  onLoadCookieData(data)
                } else {
                  LoadFromServer(apiAccess, profileKey,
                    (profile) => {
                      if (profile) {
                        onLoadCookieData(profile)
                      } else {
                        // nothing data.
                      }
                    }
                  )
                }
              }
            )
          }
        }
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialized, apiAccess, userId/*, defaultProfileId*/]
  )

  refConditions = conditions
  const onChangeCondition = useCallback(
    (axisKey?: string, condition?: DashBoardCondition, toggle?: boolean) => {
      if (!refConditions || !axisKey) return

      const clone = { ...refConditions }
      if (condition) {
        if (axisKey === "identifier") {
          clone[axisKey] = condition
        } else {
          const existCondition = clone[axisKey]
          if (existCondition) {
            if (condition.code.some((clicked) => existCondition.code.some((exist) => exist === clicked))) {
              delete clone[axisKey]
            } else if (toggle) {
              existCondition.code = existCondition.code.concat(condition.code)
              existCondition.label = existCondition.label + ',' + condition.label
            } else {
              clone[axisKey] = condition
            }
          } else {
            condition.not = toggle ? true : undefined
            clone[axisKey] = condition
          }
        }
      } else {
        delete clone[axisKey]
      }
      setConditions(clone)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const layouts = useMemo<ReactGridLayout.Layout[]>(
    () => dashBoardItems?.map((item) => ({
      i: `${item.id}`,
      w: item.width,
      h: item.height,
      x: item.x,
      y: item.y,
      maxW: 12
    })) ?? [],
    [dashBoardItems]
  )

  const onChangeLayout = useCallback(
    (layouts: ReactGridLayout.Layout[]) => onChangeDashBoardItems(
      layouts.flatMap((layout) => {
        const itemId = Number(layout.i)
        const item = dashBoardItems.find((item) => item.id === itemId)
        return item ? {
          ...item,
          width: layout.w,
          height: layout.h,
          x: layout.x,
          y: layout.y
        } : []
      })
    ),
    [onChangeDashBoardItems, dashBoardItems]
  )

  useEffect(
    () => {
      const ref = dashBoardRef.current
      if (!ref) return
      const resizeObserver = new ResizeObserver(
        () => {
          setLayoutWidth(ref.clientWidth)
          setItemHeightUnit(
            ref.clientWidth > 1400 ? 120 : 
            ref.clientWidth > 1200 ? 100 : 
            ref.clientWidth > 900 ? 90 : 
            ref.clientWidth > 680 ? 80 : 
            ref.clientWidth > 480 ? 70 : 60
          )
        }
      )
      resizeObserver.observe(ref)
      return () => {
        resizeObserver.unobserve(ref)
        resizeObserver.disconnect()
      }
    },
    [dashBoardRef, setLayoutWidth]
  )

  const onEditItem = useCallback(
    (changed: DashBoardItemData) => onChangeDashBoardItems(
      dashBoardItems.map((item) => item.id === changed.id ? changed : item)
    ),
    [dashBoardItems, onChangeDashBoardItems]
  )
  const onDeleteItem = useCallback(
    (remveId: number) => onChangeDashBoardItems(
      dashBoardItems.filter((item) => item.id !== remveId)
    ),
    [dashBoardItems, onChangeDashBoardItems]
  )

  const onCopyItem = useCallback(
    (sourceId: number) => {
      const source = dashBoardItems.find((item) => item.id === sourceId)
      if (!source) return
      const id = dashBoardItems.reduce((p, item) => Math.max(p, item.id), 0) + 1
      let x = source.x + source.width
      let y = source.y
      if ((x + source.width) > 12) {
        x = 0
        y = source.y + source.height
      }
      onChangeDashBoardItems(dashBoardItems.concat({ ...source, id, x, y }))
    },
    [dashBoardItems, onChangeDashBoardItems]
  )

  useEffect(() => {
    setClassNames('layout')
    const timer = setTimeout(() => setClassNames('layout animated'), 500)
    return () => clearTimeout(timer)
  }, [dashBoardItems])

  return (
    <Box
      ref={dashBoardRef}
      height="100%"
      width="100%"
      style={ maximizeItemId !== null ? {
        overflow: "hidden"
      } : {
        background: 'GhostWhite',
        overflow: "auto"
      }}
      {...others}
    >
      {maximizeItemId !== null ? (
        dashBoardItems.flatMap((item) => item.id === maximizeItemId ? (
          <Paper
            key={item.id}
            sx={{ width: "100%", height: "100%", overflow: 'hidden', position: 'relative' }}
          >
            <DashBoardItem
              key={`dashboard-item-${item.id}`}
              dashBoard={dashBoard}
              search={search}
              targetList={targetList}
              dataList={dataList}
              conditions={conditions}
              onChangeCondition={onChangeCondition}
              itemData={item}
              isResizing={false}
              onChangeItemData={onEditItem}
              onDeleteItem={onDeleteItem}
              onCopyItem={onCopyItem}
              onSelected={onSelected}
              isMaximize={true}
              onMaximize={() => setMaximizeItemId(null)}
            />
          </Paper>
        ) : [])
      ) : (
        <ReactGridLayout
          className={classNames}
          layout={layouts}
          cols={12}
          rowHeight={itemHeightUnit}
          margin={[8, 8]}
          width={layoutWidth}
          isResizable={true}
          draggableHandle=".draggable"
          compactType="vertical"
          resizeHandles={['s', 'w', 'n', 'e', 'se']}
          onResizeStart={(layout, oldItem) => {
            setResizingItemId(oldItem.i)
          }}
          onResizeStop={() => {
            setResizingItemId(null)
          }}
          onLayoutChange={onChangeLayout}
        >
          {layouts.map((layout, i) => {
            const id = Number(layout.i)
            const item = dashBoardItems.find((item) => item.id === id)
            return item && (
              <Paper
                key={layout.i}
                sx={{ overflow: 'hidden', position: 'relative' }}
              >
                <DashBoardItem
                  key={`dashboard-item-${i}-${item.id}`}
                  dashBoard={dashBoard}
                  targetList={targetList}
                  dataList={dataList}
                  conditions={conditions}
                  onChangeCondition={onChangeCondition}
                  itemData={item}
                  isResizing={resizingItemId === layout.i ? true : false}
                  onChangeItemData={onEditItem}
                  onDeleteItem={onDeleteItem}
                  onCopyItem={onCopyItem}
                  onSelected={onSelected}
                  isMaximize={false}
                  onMaximize={setMaximizeItemId}
                />
              </Paper>
            )
          })}
        </ReactGridLayout>
      )}
    </Box>
  )
}
